import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Box, CircularProgress, Autocomplete, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Paper, Typography,
  Divider, alpha, useTheme, InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon,
} from '@mui/icons-material';
import LocalPizzaIcon from '@mui/icons-material/LocalPizza';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';
import { branchIssuesService } from '../../services/branchIssues.service';
import { branchesService } from '../../services/branches.service';
import { itemsService, itemUnitsService } from '../../services/items.service';
import { Branch } from '../../types/branches.types';
import { Item } from '../../types/items.types';
import {
  BranchIssueDto, CreateBranchIssueDto, UpdateBranchIssueDto,
  CreateBranchIssueDetailDto, UpdateBranchIssueDetailDto,
} from '../../types/branchIssues.types';

const t = {
  en: {
    addTitle: 'New Bakery Issue', editTitle: 'Edit Bakery Issue',
    issueNumber: 'Issue Number', issueDate: 'Date',
    toBranch: 'Receiving Branch', notes: 'Notes',
    details: 'Issue Details', item: 'Item', unit: 'Unit',
    quantity: 'Quantity', unitCost: 'Cost Price', unitPrice: 'Selling Price',
    totalPrice: 'Total', addLine: 'Add Line', save: 'Save', cancel: 'Cancel',
    selectBranch: 'Select Branch', selectItem: 'Select Item',
    bakeryNote: 'Source: Bakery Branch (BR-000011)',
  },
  ar: {
    addTitle: 'إصدار مخبز جديد', editTitle: 'تعديل إصدار مخبز',
    issueNumber: 'رقم الإصدار', issueDate: 'التاريخ',
    toBranch: 'الفرع المستلم', notes: 'ملاحظات',
    details: 'تفاصيل الإصدار', item: 'الصنف', unit: 'الوحدة',
    quantity: 'الكمية', unitCost: 'سعر التكلفة', unitPrice: 'سعر البيع',
    totalPrice: 'الإجمالي', addLine: 'إضافة سطر', save: 'حفظ', cancel: 'إلغاء',
    selectBranch: 'اختر الفرع', selectItem: 'اختر الصنف',
    bakeryNote: 'المصدر: فرع المخبز (BR-000011)',
  },
};

interface UnitOption {
  unitId: number;
  unitName: string;
  unitNameAr: string;
  defaultSalePrice: number;
  defaultPurchasePrice: number;
}

interface DetailLine {
  id: string;
  itemId: number | null;
  itemName: string;
  unitId: number | null;
  unitName: string;
  units: UnitOption[];
  quantity: number;
  unitCost: number;
  unitPrice: number;
  notes: string;
  existingDetailId?: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editIssue?: BranchIssueDto | null;
}

const BranchIssueDialog: React.FC<Props> = ({ open, onClose, onSave, editIssue }) => {
  const { language } = useLanguage();
  const tr = t[language as 'en' | 'ar'];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const { minDate, maxDate } = useFiscalDateRange();
  const isSuperAdmin = useSelector((state: RootState) => state.auth.user?.isSuperAdmin) ?? false;
  const isEditMode = !!editIssue;
  const dateLocked = isEditMode && !isSuperAdmin;

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  const [issueNumber, setIssueNumber] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [branchId, setBranchId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [branches, setBranches] = useState<Branch[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [lines, setLines] = useState<DetailLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      loadMasterData();
      if (editIssue) {
        loadEditData(editIssue);
      } else {
        resetForm();
        branchIssuesService.generateNumber().then(setIssueNumber).catch(() => {});
      }
    }
  }, [open, editIssue]);

  const loadMasterData = async () => {
    setLoading(true);
    try {
      const [allBranches, allItems] = await Promise.all([
        branchesService.getAll(),
        itemsService.getBakeryItems(),
      ]);
      // استبعاد فروع المخزن (BranchType='Warehouse') من قائمة الفروع المستلمة
      setBranches(allBranches.filter((b: Branch) => b.branchType === 'Operational'));
      setItems(allItems);
    } finally {
      setLoading(false);
    }
  };

  const loadEditData = async (issue: BranchIssueDto) => {
    setIssueNumber(issue.issueNumber);
    setIssueDate(issue.issueDate.split('T')[0]);
    setBranchId(issue.branchId);
    setNotes(issue.notes || '');

    const detailLines: DetailLine[] = await Promise.all(
      issue.details.map(async (d) => {
        const unitList = await itemUnitsService.getItemUnits(d.itemId).catch(() => []);
        return {
          id: String(d.branchIssueDetailId),
          itemId: d.itemId,
          itemName: isRTL ? d.itemNameAr || d.itemName : d.itemName,
          unitId: d.unitId,
          unitName: isRTL ? d.unitNameAr || d.unitName : d.unitName,
          units: unitList.map((u: any) => ({ unitId: u.unitId, unitName: u.unitName, unitNameAr: u.unitNameAr, defaultSalePrice: u.defaultSalePrice, defaultPurchasePrice: u.defaultPurchasePrice })),
          quantity: d.quantity,
          unitCost: d.unitCost,
          unitPrice: d.unitPrice,
          notes: d.notes || '',
          existingDetailId: d.branchIssueDetailId,
        };
      })
    );
    setLines(detailLines);
  };

  const resetForm = () => {
    setIssueNumber('');
    setIssueDate(new Date().toISOString().split('T')[0]);
    setBranchId(null);
    setNotes('');
    setLines([newLine()]);
  };

  const newLine = (): DetailLine => ({
    id: Math.random().toString(),
    itemId: null, itemName: '', unitId: null, unitName: '',
    units: [], quantity: 1, unitCost: 0, unitPrice: 0, notes: '',
  });

  const handleItemChange = async (lineId: string, item: Item | null) => {
    if (!item) return;
    const unitList = await itemUnitsService.getItemUnits(item.itemId).catch(() => []);
    const mappedUnits: UnitOption[] = unitList.map((u: any) => ({
      unitId: u.unitId,
      unitName: u.unitName,
      unitNameAr: u.unitNameAr,
      defaultSalePrice: u.defaultSalePrice ?? 0,
      defaultPurchasePrice: u.defaultPurchasePrice ?? 0,
    }));
    const defaultUnit = mappedUnits.find(u => (unitList as any[]).find((r: any) => r.unitId === u.unitId)?.isDefaultSale) || mappedUnits[0] || null;
    setLines(prev => prev.map(l =>
      l.id === lineId ? {
        ...l,
        itemId: item.itemId,
        itemName: isRTL ? item.itemNameAr || item.itemName : item.itemName,
        units: mappedUnits,
        unitId: defaultUnit?.unitId ?? null,
        unitName: isRTL ? defaultUnit?.unitNameAr || defaultUnit?.unitName || '' : defaultUnit?.unitName || '',
        unitCost: defaultUnit?.defaultPurchasePrice ?? 0,
        unitPrice: defaultUnit?.defaultSalePrice ?? 0,
      } : l
    ));
  };

  const handleUnitChange = (lineId: string, unit: UnitOption | null) => {
    if (!unit) return;
    setLines(prev => prev.map(l =>
      l.id === lineId ? {
        ...l,
        unitId: unit.unitId,
        unitName: isRTL ? unit.unitNameAr || unit.unitName : unit.unitName,
        unitCost: unit.defaultPurchasePrice,
        unitPrice: unit.defaultSalePrice,
      } : l
    ));
  };

  const updateLine = (id: string, field: keyof DetailLine, value: any) => {
    setLines(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const addLine = () => setLines(prev => [...prev, newLine()]);
  const removeLine = (id: string) => setLines(prev => prev.filter(l => l.id !== id));

  const totalAmount = lines.reduce((s, l) => s + (l.unitPrice * l.quantity || 0), 0);

  const handleSave = async () => {
    if (!branchId) { toast.warning(isRTL ? 'اختر الفرع المستلم' : 'Select receiving branch'); return; }
    if (!issueNumber) { toast.warning(isRTL ? 'رقم الإصدار مطلوب' : 'Issue number required'); return; }
    if (lines.some(l => !l.itemId || !l.unitId || l.quantity <= 0 || l.unitPrice <= 0)) {
      toast.warning(isRTL ? 'تأكد من اكتمال جميع البنود (صنف، وحدة، كمية، سعر بيع)' : 'Complete all lines (item, unit, quantity, selling price)');
      return;
    }

    setSaving(true);
    try {
      if (editIssue) {

        const dto: UpdateBranchIssueDto = {
          branchIssueId: editIssue.branchIssueId,
          issueDate, branchId, notes,
          details: lines.map((l, i) => ({
            branchIssueDetailId: l.existingDetailId || 0,
            lineNumber: i + 1,
            itemId: l.itemId!,
            unitId: l.unitId!,
            quantity: l.quantity,
            unitCost: l.unitCost,
            unitPrice: l.unitPrice,
            notes: l.notes || undefined,
          } as UpdateBranchIssueDetailDto)),
        };
        await branchIssuesService.update(editIssue.branchIssueId, dto);
      } else {
        const dto: CreateBranchIssueDto = {
          issueNumber, issueDate, branchId, notes,
          details: lines.map((l, i) => ({
            lineNumber: i + 1,
            itemId: l.itemId!,
            unitId: l.unitId!,
            quantity: l.quantity,
            unitCost: l.unitCost,
            unitPrice: l.unitPrice,
            notes: l.notes || undefined,
          } as CreateBranchIssueDetailDto)),
        };
        await branchIssuesService.create(dto);
      }
      toast.success(isRTL ? (editIssue ? 'تم التعديل بنجاح' : 'تم الحفظ بنجاح') : (editIssue ? 'Updated successfully' : 'Saved successfully'));
      onSave();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || (isRTL ? 'حدث خطأ' : 'Error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
      <DialogTitle sx={{ bgcolor: '#e65100', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalPizzaIcon />
        {editIssue ? tr.editTitle : tr.addTitle}
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : (
          <>
            {/* Header */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: alpha('#e65100', 0.04) }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                🏭 {tr.bakeryNote}
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <TextField
                  label={tr.issueNumber} value={issueNumber}
                  onChange={e => setIssueNumber(e.target.value)}
                  size="small" sx={{ width: 180 }} disabled={!!editIssue}
                />
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={tr.issueDate}
                    value={issueDate ? new Date(issueDate) : null}
                    onChange={(date) => setIssueDate(date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                    format="dd/MM/yyyy"
                    minDate={minDate}
                    maxDate={maxDate}
                    views={['year', 'month', 'day']}
                    openTo="day"
                    disabled={dateLocked}
                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { width: 180 } } }}
                  />
                </LocalizationProvider>
                <Autocomplete
                  options={branches}
                  getOptionLabel={b => `${b.branchCode} - ${isRTL ? b.branchNameAr || b.branchName : b.branchName}`}
                  value={branches.find(b => b.branchId === branchId) || null}
                  onChange={(_, v) => setBranchId(v?.branchId ?? null)}
                  renderInput={params => <TextField {...params} label={tr.toBranch} size="small" />}
                  sx={{ minWidth: 250 }}
                />
                <TextField
                  label={tr.notes} value={notes}
                  onChange={e => setNotes(e.target.value)}
                  size="small" sx={{ minWidth: 200 }} multiline rows={1}
                />
              </Box>
            </Paper>

            {/* Details Table */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#e65100' }}>
              {tr.details}
            </Typography>
            <Box ref={gridRef}>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#e65100', 0.08) }}>
                    <TableCell sx={{ width: 40 }}>#</TableCell>
                    <TableCell>{tr.item}</TableCell>
                    <TableCell sx={{ width: 140 }}>{tr.unit}</TableCell>
                    <TableCell sx={{ width: 100 }}>{tr.quantity}</TableCell>
                    <TableCell sx={{ width: 120 }}>{tr.unitCost}</TableCell>
                    <TableCell sx={{ width: 120 }}>{tr.unitPrice}</TableCell>
                    <TableCell sx={{ width: 110 }}>{tr.totalPrice}</TableCell>
                    <TableCell sx={{ width: 40 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, idx) => (
                    <TableRow key={line.id}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <Autocomplete
                          options={items}
                          getOptionLabel={i => isRTL ? i.itemNameAr || i.itemName : i.itemName}
                          value={items.find(i => i.itemId === line.itemId) || null}
                          onChange={(_, v) => handleItemChange(line.id, v)}
                          renderInput={params => <TextField {...params} size="small" placeholder={tr.selectItem} />}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          options={line.units}
                          getOptionLabel={u => isRTL ? u.unitNameAr || u.unitName : u.unitName}
                          value={line.units.find(u => u.unitId === line.unitId) || null}
                          onChange={(_, v) => handleUnitChange(line.id, v)}
                          renderInput={params => <TextField {...params} size="small" />}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" value={line.quantity === 0 ? '' : String(line.quantity)}
                          onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateLine(line.id, 'quantity', v === '' || v === '.' ? 0 : parseFloat(v)); }}
                          inputProps={{ inputMode: 'decimal' }} sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" value={line.unitCost === 0 ? '' : String(line.unitCost)}
                          onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateLine(line.id, 'unitCost', v === '' || v === '.' ? 0 : parseFloat(v)); }}
                          inputProps={{ inputMode: 'decimal' }} sx={{ width: 110 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" value={line.unitPrice === 0 ? '' : String(line.unitPrice)}
                          onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateLine(line.id, 'unitPrice', v === '' || v === '.' ? 0 : parseFloat(v)); }}
                          inputProps={{ inputMode: 'decimal' }} sx={{ width: 110 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold" color="#e65100">
                          {(line.unitPrice * line.quantity).toFixed(3)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => removeLine(line.id)}
                          disabled={lines.length === 1}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Button startIcon={<AddIcon />} onClick={addLine} size="small" color="warning">
                {tr.addLine}
              </Button>
              <Box textAlign="right">
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? 'الإجمالي' : 'Total'}
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="#e65100">
                  {totalAmount.toFixed(3)} {isRTL ? 'د.ك' : 'KWD'}
                </Typography>
              </Box>
            </Box>

          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>{tr.cancel}</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}>
          {saving ? <CircularProgress size={20} /> : tr.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchIssueDialog;
